<template>
  <div class="admission-statistic">
    <v-alert v-if="error" type="error" variant="tonal" class="mb-4">
      {{ error }}
    </v-alert>

    <v-card variant="outlined" class="mb-4">
      <v-card-title>Статистика приёмной комиссии</v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="3">
            <v-select
              v-model="version"
              :items="yearOptions"
              item-title="title"
              item-value="value"
              label="Приёмная кампания"
              variant="outlined"
              density="compact"
              hide-details
              @update:model-value="loadStatistic"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="filters.department"
              :items="departments"
              item-title="name"
              item-value="id"
              label="Комплекс"
              variant="outlined"
              density="compact"
              clearable
              hide-details
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="filters.study"
              :items="studies"
              item-title="name"
              item-value="code_fis"
              label="Форма обучения"
              variant="outlined"
              density="compact"
              clearable
              hide-details
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="filters.education"
              :items="educationBases"
              item-title="short_name"
              item-value="type_ed"
              label="База образования"
              variant="outlined"
              density="compact"
              clearable
              hide-details
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-autocomplete
              v-model="filters.specialties"
              :items="specialtyOptions"
              item-title="title"
              item-value="id"
              label="Специальности"
              variant="outlined"
              density="compact"
              multiple
              chips
              closable-chips
              clearable
              hide-details
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="search"
              label="Поиск по таблице"
              prepend-inner-icon="mdi-magnify"
              variant="outlined"
              density="compact"
              clearable
              hide-details
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-data-table
      :headers="headers"
      :items="tableRows"
      :search="search"
      :loading="loading"
      :items-per-page="-1"
      fixed-header
      height="700"
    >
      <template #item.kcp="{ item }">
        <v-chip :color="capacityColor(item.kcp, item.applicants, item.students)" size="small">
          {{ item.kcp }}
        </v-chip>
      </template>
      <template #item.contract="{ item }">
        <v-chip
          :color="capacityColor(item.contract, item.contractApplicants, item.contractStudents)"
          size="small"
        >
          {{ item.contract }}
        </v-chip>
      </template>
      <template #bottom />
    </v-data-table>
  </div>
</template>

<script>
export default {
  name: "AdmissionStatistic",
  data() {
    return {
      loading: false,
      error: "",
      version: 0,
      data: [],
      years: [],
      departments: [],
      specialties: [],
      studies: [],
      educationBases: [],
      useEnrolledForCapacity: false,
      search: "",
      filters: {
        department: null,
        study: null,
        education: null,
        specialties: [],
      },
      headers: [
        { title: "Код специальности", key: "specialtyCode" },
        { title: "Наименование специальности", key: "specialtyName" },
        { title: "Форма обучения (база)", key: "specialtyForm" },
        { title: "План КЦП", key: "kcp" },
        { title: "Подано заявлений", key: "applicants" },
        { title: "Зачислено", key: "students" },
        { title: "Средний балл", key: "average" },
        { title: "Платные места", key: "contract" },
        { title: "Заявлений на платной основе", key: "contractApplicants" },
        { title: "Зачислено на платной основе", key: "contractStudents" },
        { title: "Средний балл (платно)", key: "averagec" },
      ],
    };
  },
  computed: {
    yearOptions() {
      return [
        { title: "Текущая статистика", value: 0 },
        ...this.years.map((year) => ({ title: String(year.year), value: Number(year.id) })),
      ];
    },
    specialtyOptions() {
      return this.specialties.map((specialty) => ({
        id: Number(specialty.id),
        title: `${specialty.code} ${specialty.name}`,
      }));
    },
    tableRows() {
      const grouped = new Map();
      const totals = this.emptyRow("Итого");

      this.filteredData.forEach((record) => {
        // В исходных данных могут присутствовать отдельные конкурсные группы
        // одной специальности (например, разные квалификации). Для отчёта они
        // объединяются, если совпадают специальность, форма и база обучения.
        const education = Number(record.education) === 2 ? "ОВЗ" : record.education;
        const form = `${record.study_name} (${education})`;
        const key = `${record.spec_id}_${form}`;
        if (!grouped.has(key)) {
          grouped.set(key, this.emptyRow(record.spec_name, record.code, form));
        }
        this.addRecord(grouped.get(key), record);
        this.addRecord(totals, record);
      });

      return [totals, ...grouped.values()];
    },
    filteredData() {
      return this.data.filter((record) => {
        if (this.filters.department !== null
          && Number(record.department_id) !== Number(this.filters.department)) return false;
        if (this.filters.study !== null
          && String(record.code_fis) !== String(this.filters.study)) return false;
        if (this.filters.education !== null
          && Number(record.base_education) !== Number(this.filters.education)) return false;
        return !this.filters.specialties.length
          || this.filters.specialties.map(Number).includes(Number(record.spec_id));
      });
    },
  },
  created() {
    this.loadStatistic();
  },
  methods: {
    async loadStatistic() {
      this.loading = true;
      this.error = "";
      try {
        const response = await this.axios.post(
          `${this.$model.wwwroot}/procollege/manage_groups/admission/statistic/getStatistic`,
          new URLSearchParams({ version: String(this.version) })
        );
        const result = response.data || {};
        this.data = Array.isArray(result.data) ? result.data : [];
        this.years = Array.isArray(result.years) ? result.years : [];
        this.departments = Array.isArray(result.departments) ? result.departments : [];
        this.specialties = Array.isArray(result.specialties) ? result.specialties : [];
        this.studies = Array.isArray(result.studies) ? result.studies : [];
        this.educationBases = Array.isArray(result.educationBases) ? result.educationBases : [];
        this.useEnrolledForCapacity = Boolean(result.useEnrolledForCapacity);
      } catch (error) {
        this.error = "Не удалось загрузить статистику приёмной комиссии.";
        if (this.axiosDefaultErrorHandler) this.axiosDefaultErrorHandler(error);
      } finally {
        this.loading = false;
      }
    },
    emptyRow(name, code = "", form = "") {
      return {
        specialtyCode: code,
        specialtyName: name,
        specialtyForm: form,
        kcp: 0,
        applicants: 0,
        students: 0,
        contract: 0,
        contractApplicants: 0,
        contractStudents: 0,
        average: "",
        averagec: "",
      };
    },
    addRecord(row, record) {
      // Бюджетный и договорной набор учитываются независимо: у каждого свой
      // план, количество заявлений, зачисленных и средний балл.
      row.kcp += Number(record.budget) || 0;
      row.applicants += Number(record.applicant) || 0;
      row.students += Number(record.students) || 0;
      row.contract += Number(record.contract) || 0;
      row.contractApplicants += Number(record.applicantc) || 0;
      row.contractStudents += Number(record.studentsc) || 0;
      if (row.average === "" && Number(record.average)) row.average = record.average;
      if (row.averagec === "" && Number(record.averagec)) row.averagec = record.averagec;
    },
    capacityColor(capacity, applicants, students) {
      // До 1 сентября выполнение плана оценивается по поданным заявлениям,
      // после завершения основной кампании — по фактически зачисленным.
      const actual = this.useEnrolledForCapacity ? students : applicants;
      return Number(capacity) > Number(actual) ? "error" : "success";
    },
  },
};
</script>

<style scoped>
.admission-statistic {
  width: 100%;
}
</style>
